// Permission gate: does the signed-in user hold VERB on MODULE in the web admin
// panel? The module-level gates (`accessibleNavItems`, `canAccess` in App.tsx)
// only answer "may they reach this module at all"; this is the finer question,
// reading the `permissions` array on each grant that those gates discard.
//
// `Verb` is imported from rolesApi rather than redeclared, so this check and the
// Roles capability matrix share one vocabulary. The `string & {}` arm keeps
// autocomplete on the four core verbs while still accepting a module's extras —
// MatrixModule.extras proves non-core permission slugs exist.
//
// `moduleSlug` stays a plain string, not a hand-written union: the slugs are
// backend-defined (GET /modules) and NavItem.moduleSlug already carries them
// untyped, so a union here would silently drift from the server's list.
//
// Absent access data (`null`) DENIES. This deliberately diverges from
// accessibleNavItems, which fails open on a null slug list so a contract hiccup
// can't brick the sidebar: a nav item that shouldn't be there is a wasted click,
// whereas an edit affordance that shouldn't be there invites a write the server
// will reject. So a missing payload and an empty map answer the same way here —
// no grant proven, no access.
//
// No owner bypass: isAccessible doesn't grant owners blanket module access
// (only `ownerOnly` items), so an owner's verbs come through `permissions` like
// anyone else's.

import { useAppSelector } from '@/store/hooks';
import { selectAdminPermissions } from '@/redux/auth/selectors';
import type { Verb } from '@/api/endpoints/rolesApi';

/** A core verb, or any module-specific extra slug the capability matrix defines. */
export type PermissionVerb = Verb | (string & {});

const useCan = (moduleSlug: string, verb: PermissionVerb): boolean => {
  const byModule = useAppSelector(selectAdminPermissions);
  return byModule?.[moduleSlug]?.includes(verb) ?? false;
};

export default useCan;
